import { format, differenceInDays, differenceInMonths, differenceInYears, parseISO } from 'date-fns';
import { zhCN } from 'date-fns/locale';

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'yyyy年MM月dd日', { locale: zhCN });
}

export function formatDateShort(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'MM/dd', { locale: zhCN });
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'HH:mm', { locale: zhCN });
}

export function formatDateTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'yyyy/MM/dd HH:mm', { locale: zhCN });
}

export function getRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  const now = new Date();
  const diffDays = differenceInDays(now, d);
  
  if (diffDays === 0) return '今天';
  if (diffDays === 1) return '昨天';
  if (diffDays < 7) return `${diffDays}天前`;
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}周前`;
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}个月前`;
  return `${Math.floor(diffDays / 365)}年前`;
}

export function calculateAge(birthDate: string, targetDate?: string | Date): string {
  const birth = parseISO(birthDate);
  const target = targetDate ? (typeof targetDate === 'string' ? parseISO(targetDate) : targetDate) : new Date();
  
  const years = differenceInYears(target, birth);
  const months = differenceInMonths(target, birth) % 12;
  const days = differenceInDays(target, birth) % 30;
  
  if (years > 0) {
    if (months > 0) return `${years}岁${months}个月`;
    return `${years}岁`;
  }
  if (months > 0) {
    if (days > 0) return `${months}个月${days}天`;
    return `${months}个月`;
  }
  return `${days}天`;
}

export function getDaysOld(birthDate: string, targetDate?: string | Date): number {
  const birth = parseISO(birthDate);
  const target = targetDate ? (typeof targetDate === 'string' ? parseISO(targetDate) : targetDate) : new Date();
  return differenceInDays(target, birth);
}

export function getMonthKey(date: string | Date): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'yyyy-MM', { locale: zhCN });
}

export function formatMonthKey(monthKey: string): string {
  const [year, month] = monthKey.split('-');
  return `${year}年${parseInt(month)}月`;
}

export function getCalendarDays(year: number, month: number): Array<{
  date: string;
  day: number;
  isCurrentMonth: boolean;
}> {
  const firstDay = new Date(year, month - 1, 1);
  const lastDay = new Date(year, month, 0);
  const startPadding = firstDay.getDay();
  
  const days: Array<{ date: string; day: number; isCurrentMonth: boolean }> = [];
  
  // 上月填充
  const prevMonthLastDay = new Date(year, month - 1, 0).getDate();
  for (let i = startPadding - 1; i >= 0; i--) {
    const day = prevMonthLastDay - i;
    const date = new Date(year, month - 2, day);
    days.push({
      date: format(date, 'yyyy-MM-dd'),
      day,
      isCurrentMonth: false,
    });
  }
  
  // 当月
  for (let i = 1; i <= lastDay.getDate(); i++) {
    const date = new Date(year, month - 1, i);
    days.push({
      date: format(date, 'yyyy-MM-dd'),
      day: i,
      isCurrentMonth: true,
    });
  }
  
  // 下月填充
  const remaining = 42 - days.length;
  for (let i = 1; i <= remaining; i++) {
    const date = new Date(year, month, i);
    days.push({
      date: format(date, 'yyyy-MM-dd'),
      day: i,
      isCurrentMonth: false,
    });
  }
  
  return days;
}
